import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_ITEMS, SITE } from "@/config/site";
import { AccessLink, SoonBadge } from "@/components/AccessLink";
import { IconByName } from "@/components/icons";
import { openMaterialsModal } from "@/components/MaterialsModal";
import { scrollToSection, stopScroll, resumeScroll } from "@/lib/scroll";
import { cn } from "@/lib/utils";

function BrandMark() {
  return (
    <span className="flex items-center gap-3">
      {SITE.logoSrc && (
        <img
          src={SITE.logoSrc}
          alt="Logo FW Team Fitness"
          className="h-11 w-11 rounded-xl object-cover"
        />
      )}
      <span className="hidden whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.24em] text-slate-400 sm:block">
        {SITE.trainerName} — {SITE.cref}
      </span>
    </span>
  );
}

function DesktopNavItem({ item, onInternal }) {
  if (item.opensMaterials) {
    return (
      <button
        type="button"
        data-testid={`nav-link-${item.id}`}
        onClick={openMaterialsModal}
        className="flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-brand-blue"
      >
        {item.label}
      </button>
    );
  }
  return (
    <AccessLink
      href={item.href}
      external={item.external}
      testId={`nav-link-${item.id}`}
      onInternalNavigate={onInternal}
      badgeClassName="ml-1.5"
      className="flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-brand-blue aria-disabled:opacity-60"
    >
      {item.label}
    </AccessLink>
  );
}

function MobileDrawer({ onClose, onInternal }) {
  return (
    <motion.div
      id="mobile-menu"
      data-testid="mobile-menu-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="Menu de navegação"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      style={{ backgroundColor: "rgba(7, 9, 14, 0.97)" }}
      className="fixed inset-x-0 bottom-0 top-16 z-40 backdrop-blur-xl xl:hidden"
    >
      <div className="flex h-full flex-col gap-2.5 overflow-y-auto px-5 pb-10 pt-6">
        {NAV_ITEMS.map((item, i) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i, duration: 0.35 }}
          >
            {item.opensMaterials ? (
              <button
                type="button"
                data-testid={`mobile-nav-link-${item.id}`}
                onClick={() => {
                  onClose();
                  openMaterialsModal();
                }}
                className="flex min-h-[56px] w-full items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 text-lg font-semibold text-white transition-colors active:border-brand-teal/60 active:bg-brand-teal/15"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue">
                  <IconByName name={item.icon} className="h-5 w-5" />
                </span>
                {item.label}
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-slate-500" />
              </button>
            ) : (
            <AccessLink
              href={item.href}
              external={item.external}
              testId={`mobile-nav-link-${item.id}`}
              onInternalNavigate={onInternal}
              onClick={item.external ? onClose : undefined}
              badgeClassName="ml-auto"
              className={cn(
                "flex min-h-[56px] items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 text-lg font-semibold text-white transition-colors",
                item.href ? "active:border-brand-teal/60 active:bg-brand-teal/15" : "opacity-70",
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-brand-teal/40 bg-brand-teal/15 text-brand-blue">
                <IconByName name={item.icon} className="h-5 w-5" />
              </span>
              {item.label}
              {item.href && (
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-slate-500" />
              )}
            </AccessLink>
            )}
          </motion.div>
        ))}
        <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500">
          {SITE.slogan}
        </p>
      </div>
    </motion.div>
  );
}

export default function SiteHeader() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    stopScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      resumeScroll();
    };
  }, [open]);

  const handleInternal = (hash) => {
    setOpen(false);
    setTimeout(() => scrollToSection(hash), 120);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#07090e]/80 backdrop-blur-xl"
      >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        <AccessLink
          href="#inicio"
          testId="header-brand"
          onInternalNavigate={handleInternal}
          showBadge={false}
          className="flex items-center gap-3"
          ariaLabel="FW Team Fitness — voltar ao início"
        >
          <BrandMark />
        </AccessLink>

        <nav
          className="hidden items-center gap-0.5 xl:flex"
          data-testid="site-header-nav"
          aria-label="Navegação principal"
        >
          {NAV_ITEMS.map((item) => (
            <DesktopNavItem key={item.id} item={item} onInternal={handleInternal} />
          ))}
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-trigger"
          aria-expanded={open}
          aria-controls="mobile-menu"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 text-white transition-colors hover:border-brand-teal/60 hover:text-brand-blue xl:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      </header>

      <AnimatePresence>
        {open && (
          <MobileDrawer onClose={() => setOpen(false)} onInternal={handleInternal} />
        )}
      </AnimatePresence>
    </>
  );
}
